import { defModule, type TaskDeclaration, type TaskMessage, type UpdateResult } from '@st/redux'
import {
  CURRENT_TAX_YEAR,
  DEFAULT_RETURN_TYPE,
  type ReturnType,
  type TaxYear
} from '@st/tax-folder'
import { match, P } from '@st/pattern'
import { copyTextToClipboard } from '~/features/copy-to-clipboard'
import { observeOrganizationRoute } from '~/features/module-runtime'
import type { MagicLinkPortal } from '~/features/routes'

type LinkPortal = Exclude<MagicLinkPortal, 'karbon'>

export type MagicLinkDelivery = { type: 'selfEmail' } | { type: 'portal'; portal: LinkPortal }

type SchemaSelection = {
  delivery: MagicLinkDelivery
  taxYear: TaxYear
  returnType: ReturnType
}

export type MagicLinksState =
  | { step: 'idle' }
  | { step: 'selectSharingMethod' }
  | { step: 'stanfordtaxGuidance' }
  | { step: 'selectPortal' }
  | { step: 'karbonGuidance' }
  | ({ step: 'selectSchema' } & SchemaSelection)
  | ({ step: 'copyLinks' } & SchemaSelection)

const observeMagicLinksRoute = observeOrganizationRoute({ name: 'magic_links' })

export type MagicLinksMessage =
  | TaskMessage<typeof observeMagicLinksRoute>
  | {
      type: 'sharingMethodSelected'
      method: 'stanfordtax' | 'selfEmail' | 'portal'
    }
  | { type: 'portalSelected'; portal: MagicLinkPortal }
  | { type: 'taxYearSelected'; taxYear: TaxYear }
  | { type: 'returnTypeSelected'; returnType: ReturnType }
  | { type: 'generateLinksRequested' }
  | { type: 'copyLinkRequested'; url: string }
  | { type: 'backRequested' }

export const magicLinksModule = defModule<{ state: MagicLinksState; message: MagicLinksMessage }>({
  init: () => ({ step: 'idle' }),
  update: updateMagicLinks,
  tasks: (): TaskDeclaration<MagicLinksMessage>[] => [observeMagicLinksRoute()]
})

// The route owns only entry and exit. Every wizard step is intentionally ephemeral: browser
// refresh starts over, browser Back leaves the wizard, and changing organizations resets it.
function updateMagicLinks(
  state: MagicLinksState,
  message: MagicLinksMessage
): UpdateResult<MagicLinksState, MagicLinksMessage> {
  return match({ state, message })
    .returnType<UpdateResult<MagicLinksState, MagicLinksMessage>>()
    .with({ message: { type: 'organizationRouteChanged' } }, ({ state, message }) => {
      if (message.context.status != 'loaded') return { step: 'idle' }
      if (state.step == 'idle') return { step: 'selectSharingMethod' }
      return state
    })
    .with(
      {
        state: { step: 'selectSharingMethod' },
        message: { type: 'sharingMethodSelected', method: 'stanfordtax' }
      },
      () => ({ step: 'stanfordtaxGuidance' })
    )
    .with(
      {
        state: { step: 'selectSharingMethod' },
        message: { type: 'sharingMethodSelected', method: 'selfEmail' }
      },
      () => initialSchemaSelection({ type: 'selfEmail' })
    )
    .with(
      {
        state: { step: 'selectSharingMethod' },
        message: { type: 'sharingMethodSelected', method: 'portal' }
      },
      () => ({ step: 'selectPortal' })
    )
    .with(
      { state: { step: 'selectPortal' }, message: { type: 'portalSelected', portal: 'karbon' } },
      () => ({ step: 'karbonGuidance' })
    )
    .with(
      {
        state: { step: 'selectPortal' },
        message: { type: 'portalSelected', portal: P.not('karbon') }
      },
      ({ message }) => initialSchemaSelection({ type: 'portal', portal: message.portal })
    )
    .with(
      { state: { step: 'selectSchema' }, message: { type: 'taxYearSelected' } },
      ({ state, message }) => ({ ...state, taxYear: message.taxYear })
    )
    .with(
      { state: { step: 'selectSchema' }, message: { type: 'returnTypeSelected' } },
      ({ state, message }) => ({ ...state, returnType: message.returnType })
    )
    .with(
      { state: { step: 'selectSchema' }, message: { type: 'generateLinksRequested' } },
      ({ state }) => ({ ...state, step: 'copyLinks' })
    )
    .with(
      { state: { step: 'copyLinks' }, message: { type: 'copyLinkRequested' } },
      ({ state, message }) => [
        state,
        copyTextToClipboard(message.url, 'Copied magic link to clipboard')
      ]
    )
    .with(
      {
        state: { step: P.union('stanfordtaxGuidance', 'selectPortal') },
        message: { type: 'backRequested' }
      },
      () => ({ step: 'selectSharingMethod' })
    )
    .with({ state: { step: 'karbonGuidance' }, message: { type: 'backRequested' } }, () => ({
      step: 'selectPortal'
    }))
    .with(
      {
        state: { step: 'selectSchema', delivery: { type: 'selfEmail' } },
        message: { type: 'backRequested' }
      },
      () => ({ step: 'selectSharingMethod' })
    )
    .with(
      {
        state: { step: 'selectSchema', delivery: { type: 'portal' } },
        message: { type: 'backRequested' }
      },
      () => ({ step: 'selectPortal' })
    )
    .with({ state: { step: 'copyLinks' }, message: { type: 'backRequested' } }, ({ state }) => ({
      ...state,
      step: 'selectSchema'
    }))
    .otherwise(({ state, message }) => {
      throw new Error(`Unexpected magic-links message ${message.type} while ${state.step}`)
    })
}

function initialSchemaSelection(delivery: MagicLinkDelivery): MagicLinksState {
  return {
    step: 'selectSchema',
    delivery,
    taxYear: CURRENT_TAX_YEAR,
    returnType: DEFAULT_RETURN_TYPE
  }
}
